import { idadeEmMeses, type Indices } from '@equiview/domain';
import type { Trait } from '@equiview/protocol';
import { traitsV1 } from '@equiview/protocol';
import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { AnexarMidia } from '../components/AnexarMidia.js';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { Genealogia } from '../components/Genealogia.js';
import { GraficoEvolucao } from '../components/GraficoEvolucao.js';
import { IconeCamera, IconeComparar, IconeDocumento, IconeVideo } from '../components/Icones.js';
import { NavInterna } from '../components/NavInterna.js';
import {
  Aguardar,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  classeTd,
  classeTh,
  Dado,
  Etiqueta,
  EstadoVazio,
  Foto,
  Indicador,
  Painel,
  Tabela,
} from '../components/ui/index.js';
import {
  classeNaData,
  faixaNaData,
  formatarData,
  formatarIdade,
  formatarNascimento,
  formatarValor,
  hojeIso,
  idadeDoAnimal,
  indicesDaFicha,
  ORDEM_GRUPOS,
  ROTULO_ANDAMENTO,
  ROTULO_CLASSE,
  ROTULO_FAIXA,
  ROTULO_GRUPO,
  ROTULO_INDICE,
  ROTULO_SEXO,
  ROTULO_VISTA,
  traitPorCodigo,
  unidadeCurta,
  valoresDaFicha,
} from '../dados/catalogo.js';
import { useConsulta, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Medida, Midia } from '../dados/fonte.js';
import {
  frasePosicao,
  fraseProle,
  MEDIDAS_DA_PROLE,
  montarPopulacao,
  posicaoNaIdade,
  proleNaMedida,
  serieDeReferencia,
  type Populacao,
} from '../dados/populacao.js';

const SECOES = [
  { id: 'resumo', rotulo: 'Resumo' },
  { id: 'medidas', rotulo: 'Medidas' },
  { id: 'evolucao', rotulo: 'Evolução' },
  { id: 'midias', rotulo: 'Mídias' },
  { id: 'genealogia', rotulo: 'Genealogia' },
] as const;

export function FichaAnimal() {
  const { id = '' } = useParams();
  const consulta = useConsulta(
    async (f) => {
      const [animal, todos, todas, capas] = await Promise.all([f.animal(id), f.listarAnimais(), f.avaliacoes(), f.capas()]);
      // A populacao inteira: e a regua do "esperado para a idade" e da media da prole.
      const medidasDeTodos = await f.medidas(todas.map((a) => a.id));
      const avaliacoes = todas.filter((a) => a.animal_id === id);
      const doAnimal = new Set(avaliacoes.map((a) => a.id));
      const medidas = medidasDeTodos.filter((m) => doAnimal.has(m.assessment_id));
      return { animal, todos, avaliacoes, medidas, capa: capas.get(id) ?? null, pop: montarPopulacao(todos, todas, medidasDeTodos) };
    },
    [id],
  );

  return (
    <Aguardar consulta={consulta}>
      {({ animal, ...resto }) =>
        animal ? (
          <Ficha animal={animal} {...resto} />
        ) : (
          <EstadoVazio titulo="Animal não encontrado" acao={<BotaoLink to="/animais">Voltar ao plantel</BotaoLink>} />
        )
      }
    </Aguardar>
  );
}

function Ficha({
  animal,
  todos,
  avaliacoes,
  medidas,
  capa,
  pop,
}: {
  animal: Animal;
  todos: Animal[];
  avaliacoes: Avaliacao[];
  medidas: Medida[];
  capa: Midia | null;
  pop: Populacao;
}) {
  const [params, setParams] = useSearchParams();
  const equipe = usePodeCadastrar();
  const hoje = hojeIso();
  const ficha = avaliacoes.find((a) => a.id === params.get('ficha')) ?? avaliacoes.at(-1) ?? null;
  const anterior = ficha ? avaliacoes[avaliacoes.indexOf(ficha) - 1] : undefined;
  const selecionarFicha = (fichaId: string) => {
    const p = new URLSearchParams(params);
    p.set('ficha', fichaId);
    setParams(p, { replace: true });
  };

  const pai = todos.find((a) => a.id === animal.sire_id);
  const mae = todos.find((a) => a.id === animal.dam_id);
  const filhos = todos.filter((a) => a.sire_id === animal.id || a.dam_id === animal.id);
  const classeHoje = classeNaData(animal, todos, hoje);
  const faixaHoje = classeHoje === 'potro' ? faixaNaData(animal, hoje) : null;

  const medidasDe = (av: Avaliacao | undefined) => (av ? medidas.filter((m) => m.assessment_id === av.id) : []);
  const medidasDaFicha = useMemo(() => medidasDe(ficha ?? undefined), [medidas, ficha]);
  const medidasAnteriores = useMemo(() => medidasDe(anterior), [medidas, anterior]);

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[{ rotulo: 'Plantel', para: '/animais' }, { rotulo: animal.name }]}
        sobretitulo="Ficha do animal"
        titulo={animal.name}
        etiquetas={
          <>
            <Etiqueta tom={classeHoje === 'potro' ? 'destaque' : 'marca'}>{ROTULO_CLASSE[classeHoje]}</Etiqueta>
            {faixaHoje && <Etiqueta>{ROTULO_FAIXA[faixaHoje]}</Etiqueta>}
          </>
        }
        meta={
          <span className="numero">
            <span className="font-mono">{animal.abccmm_registry ?? 'Sem registro'}</span> · {ROTULO_SEXO[animal.sex]} ·{' '}
            {idadeDoAnimal(animal, hoje)}
          </span>
        }
        acoes={
          <>
            {equipe && <BotaoLink to={`/animais/${animal.id}/editar`}>Editar</BotaoLink>}
            {ficha ? (
              <BotaoLink to={`/animais/${animal.id}/laudo?ficha=${ficha.id}`}>
                <IconeDocumento width={18} height={18} /> Gerar laudo
              </BotaoLink>
            ) : (
              <Botao disabled title="O laudo sai depois da primeira avaliação">
                <IconeDocumento width={18} height={18} /> Gerar laudo
              </Botao>
            )}
            <BotaoLink variante="principal" to={`/comparar?a=${animal.id}${ficha ? `&fa=${ficha.id}` : ''}`}>
              <IconeComparar width={18} height={18} /> Comparar
            </BotaoLink>
          </>
        }
      />

      <NavInterna secoes={SECOES} />

      <section id="resumo" aria-label="Resumo" className="scroll-mt-28 space-y-6">
        <Painel corpo="p-0">
          <div className="grid md:grid-cols-[minmax(0,280px)_minmax(0,1fr)]">
            <FotoDaMidia
              midia={capa}
              alt={`${animal.name}, perfil esquerdo`}
              // Sem foto, no celular so uma faixa baixa: o espaco de uma foto vazia empurra a ficha para baixo.
              className={`w-full border-b border-borda md:aspect-auto md:h-full md:border-r md:border-b-0 ${capa ? 'aspect-4/3' : 'h-24'}`}
              legenda="Foto ainda não enviada"
            />
            <dl className="grid grid-cols-2 gap-px bg-borda sm:grid-cols-4">
              {(
                [
                  ['Registro', <span className="font-mono">{animal.abccmm_registry ?? '—'}</span>],
                  ['Sexo', ROTULO_SEXO[animal.sex]],
                  ['Nascimento', formatarNascimento(animal)],
                  ['Idade hoje', idadeDoAnimal(animal, hoje)],
                  ['Pelagem', animal.coat_color ?? '—'],
                  ['Marcha declarada', rotuloMarcha(animal.marcha_type_declared)],
                  ['Pai', pai ? <LinkAnimal animal={pai} /> : '—'],
                  ['Mãe', mae ? <LinkAnimal animal={mae} /> : '—'],
                  ['Filhos cadastrados', filhos.length],
                  ['Avaliações', avaliacoes.length],
                  ['Primeira avaliação', avaliacoes[0] ? formatarData(avaliacoes[0].visit_date) : '—'],
                  ['Última avaliação', avaliacoes.at(-1) ? formatarData(avaliacoes.at(-1)!.visit_date) : '—'],
                ] as const
              ).map(([rotulo, valor]) => (
                <div key={rotulo} className="bg-superficie px-4 py-3">
                  <Dado rotulo={rotulo}>{valor}</Dado>
                </div>
              ))}
            </dl>
          </div>
        </Painel>

        {ficha ? (
          <>
            <LinhaDoTempo animal={animal} todos={todos} avaliacoes={avaliacoes} ficha={ficha} onSelecionar={selecionarFicha} />
            <IndicesDaFicha atual={medidasDaFicha} anterior={anterior ? medidasAnteriores : null} />
          </>
        ) : (
          <EstadoVazio titulo="Ainda não avaliado">
            As medidas, a evolução e as mídias aparecem depois da primeira avaliação.
          </EstadoVazio>
        )}
      </section>

      {ficha && (
        <>
          <MedidasDaFicha id="medidas" ficha={ficha} medidas={medidasDaFicha} anteriores={anterior ? medidasAnteriores : null} />
          <Evolucao
            id="evolucao"
            animal={animal}
            avaliacoes={avaliacoes}
            medidas={medidas}
            ficha={ficha}
            pop={pop}
            onSelecionar={selecionarFicha}
          />
          <Midias id="midias" animalId={animal.id} fichaId={ficha.id} equipe={equipe} />
        </>
      )}

      <section id="genealogia" aria-label="Genealogia" className="scroll-mt-28 space-y-6">
        <Painel titulo="Genealogia">
          <Genealogia animal={animal} todos={todos} />
        </Painel>
        <Prole filhos={filhos} todos={todos} pop={pop} />
      </section>
    </div>
  );
}

/** "+3,1 cm" (acima), "−2,0 cm" (abaixo), "—" sem referencia. */
function DiferencaDoEsperado({ desvio, codigo }: { desvio: { diferenca: number; leitura: string } | undefined; codigo: string }) {
  if (!desvio) return <span className="text-apagado">—</span>;
  const t = traitPorCodigo.get(codigo);
  const sinal = desvio.diferenca > 0 ? '+' : desvio.diferenca < 0 ? '−' : '';
  const cor = desvio.leitura === 'acima' ? 'text-ok' : desvio.leitura === 'abaixo' ? 'text-aviso' : 'text-apagado';
  return (
    <span className={cor}>
      {sinal}
      {formatarValor(Math.abs(desvio.diferenca), t)}
      {unidadeCurta(t)}
    </span>
  );
}

const rotuloMarcha = (m: Animal['marcha_type_declared']) => (m === 'batida' ? 'Batida' : m === 'picada' ? 'Picada' : '—');

function LinkAnimal({ animal }: { animal: Animal }) {
  return (
    <Link to={`/animais/${animal.id}`} className="text-destaque-texto underline-offset-2 hover:underline">
      {animal.name}
    </Link>
  );
}

/** Linha do tempo das avaliacoes. A classe e calculada na data de cada ficha. */
function LinhaDoTempo({
  animal,
  todos,
  avaliacoes,
  ficha,
  onSelecionar,
}: {
  animal: Animal;
  todos: Animal[];
  avaliacoes: Avaliacao[];
  ficha: Avaliacao;
  onSelecionar: (id: string) => void;
}) {
  const classe = classeNaData(animal, todos, ficha.visit_date);
  const faixa = classe === 'potro' ? faixaNaData(animal, ficha.visit_date) : null;
  return (
    <Painel
      titulo="Período de vida"
      acoes={
        <span className="text-sm text-apagado">
          Nesta ficha: <strong className="font-semibold text-texto">{ROTULO_CLASSE[classe]}</strong>
          {faixa && ` · ${ROTULO_FAIXA[faixa]}`}
        </span>
      }
    >
      <ol role="radiogroup" aria-label="Avaliação" className="relative flex overflow-x-auto pt-1 pb-1">
        {avaliacoes.map((a, i) => {
          const ativa = a.id === ficha.id;
          return (
            <li key={a.id} className="relative min-w-40 flex-1">
              {/* Trilho: liga um marcador ao proximo. */}
              {i < avaliacoes.length - 1 && <span aria-hidden className="absolute top-2.75 right-0 left-1/2 h-px bg-borda-forte" />}
              {i > 0 && <span aria-hidden className="absolute top-2.75 right-1/2 left-0 h-px bg-borda-forte" />}
              <button
                type="button"
                role="radio"
                aria-checked={ativa}
                onClick={() => onSelecionar(a.id)}
                className="group relative flex w-full flex-col items-center gap-1.5 px-2 pb-1 text-center"
              >
                <span
                  aria-hidden
                  className={`size-3.5 rotate-45 border-2 transition-colors ${
                    ativa ? 'border-marca bg-marca' : 'border-borda-forte bg-superficie group-hover:border-destaque'
                  }`}
                />
                <span className={`text-sm font-semibold ${ativa ? 'text-marca' : 'text-texto'}`}>
                  {formatarIdade(idadeEmMeses(animal.birth_date, a.visit_date))}
                </span>
                <span className="numero text-xs text-apagado">
                  {formatarData(a.visit_date)} · Nível {a.protocol_level}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </Painel>
  );
}

const CHAVES_INDICE = Object.keys(ROTULO_INDICE) as Array<keyof Indices>;
const casasIndice = (k: keyof Indices) => (k === 'ossatura' ? 1 : 3);

function IndicesDaFicha({ atual, anterior }: { atual: Medida[]; anterior: Medida[] | null }) {
  const ind = indicesDaFicha(valoresDaFicha(atual));
  const ant = anterior ? indicesDaFicha(valoresDaFicha(anterior)) : null;
  const fmt = (v: number, k: keyof Indices) =>
    v.toLocaleString('pt-BR', { minimumFractionDigits: casasIndice(k), maximumFractionDigits: casasIndice(k) });
  return (
    <section aria-label="Índices morfométricos" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="rotulo-secao">Índices morfométricos</h2>
        <p className="text-xs text-apagado">
          Calculados na hora, a partir das medidas brutas{ant ? ' · variação em relação à ficha anterior' : ''}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda sm:grid-cols-3 lg:grid-cols-5">
        {CHAVES_INDICE.map((k) => {
          const v = ind[k];
          const a = ant?.[k];
          const d = v != null && a != null ? v - a : null;
          return (
            <Indicador
              key={k}
              rotulo={ROTULO_INDICE[k]}
              valor={v == null ? '—' : fmt(v, k)}
              variacao={
                d == null
                  ? null
                  : {
                      direcao: Math.abs(d) < 10 ** -casasIndice(k) ? 'igual' : d > 0 ? 'sobe' : 'desce',
                      texto: `${d > 0 ? '+' : ''}${fmt(d, k)}`,
                    }
              }
            />
          );
        })}
      </div>
    </section>
  );
}

function textoMedida(ms: Medida[], trait: Trait): string {
  const um = (m: Medida | undefined) =>
    m ? `${formatarValor(m.value_num, trait)}${m.value_num == null ? '' : unidadeCurta(trait)}` : '—';
  if (!trait.sided) return um(ms.find((m) => m.side === 'NA'));
  return `E ${um(ms.find((m) => m.side === 'E'))} · D ${um(ms.find((m) => m.side === 'D'))}`;
}

function MedidasDaFicha({
  id,
  ficha,
  medidas,
  anteriores,
}: {
  id: string;
  ficha: Avaliacao;
  medidas: Medida[];
  anteriores: Medida[] | null;
}) {
  const [comparar, setComparar] = useState(false);
  const mostrarAnterior = comparar && anteriores != null;
  const agrupar = (lista: Medida[]) => {
    const r = new Map<string, Medida[]>();
    for (const m of lista) r.set(m.trait_code, [...(r.get(m.trait_code) ?? []), m]);
    return r;
  };
  const atual = agrupar(medidas);
  const ant = agrupar(anteriores ?? []);

  const grupos = ORDEM_GRUPOS.map((g) => ({
    grupo: g,
    traits: traitsV1.filter((t) => t.group === g && atual.has(t.code)).sort((a, b) => a.sortOrder - b.sortOrder),
  })).filter((g) => g.traits.length > 0);

  return (
    <section id={id} aria-labelledby={`${id}-titulo`} className="scroll-mt-28 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-borda pb-2">
        <div>
          <h2 id={`${id}-titulo`} className="font-serif text-xl font-semibold text-marca">
            Medidas da ficha
          </h2>
          <p className="numero text-xs text-apagado">
            {formatarData(ficha.visit_date)} · nível {ficha.protocol_level} · {medidas.length} observações
          </p>
        </div>
        <label className={`flex items-center gap-2 text-sm ${anteriores ? '' : 'opacity-50'}`}>
          <input
            type="checkbox"
            checked={comparar}
            disabled={!anteriores}
            onChange={(e) => setComparar(e.target.checked)}
            className="size-4 min-h-0 accent-(--color-marca)"
          />
          Comparar com a ficha anterior
        </label>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        {grupos.map(({ grupo, traits }) => (
          <Painel key={grupo} titulo={ROTULO_GRUPO[grupo]} corpo="p-0">
            <Tabela rotulo={ROTULO_GRUPO[grupo]}>
              <thead>
                <tr>
                  <th scope="col" className={classeTh}>
                    Medida
                  </th>
                  <th scope="col" className={`${classeTh} text-right`}>
                    Atual
                  </th>
                  {mostrarAnterior && (
                    <>
                      <th scope="col" className={`${classeTh} text-right`}>
                        Anterior
                      </th>
                      <th scope="col" className={`${classeTh} text-right`}>
                        Δ
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {traits.map((t) => {
                  const a = atual.get(t.code) ?? [];
                  const p = ant.get(t.code) ?? [];
                  const va = a.find((m) => m.side === 'NA')?.value_num;
                  const vp = p.find((m) => m.side === 'NA')?.value_num;
                  const delta = !t.sided && va != null && vp != null ? va - vp : null;
                  return (
                    <tr key={t.code} className="last:*:border-b-0">
                      <th scope="row" className={`${classeTd} text-left font-normal`} title={t.definition}>
                        {t.label}
                      </th>
                      <td className={`${classeTd} numero text-right font-medium whitespace-nowrap`}>{textoMedida(a, t)}</td>
                      {mostrarAnterior && (
                        <>
                          <td className={`${classeTd} numero text-right whitespace-nowrap text-apagado`}>
                            {p.length ? textoMedida(p, t) : '—'}
                          </td>
                          <td className={`${classeTd} numero text-right whitespace-nowrap`}>
                            {delta == null ? '—' : `${delta > 0 ? '+' : ''}${formatarValor(delta, t)}${unidadeCurta(t)}`}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </Painel>
        ))}
      </div>
    </section>
  );
}

const CONTINUAS = traitsV1.filter((t) => t.kind === 'linear' || t.kind === 'perimetro' || t.kind === 'angulo');

function Evolucao({
  id,
  animal,
  avaliacoes,
  medidas,
  ficha,
  pop,
  onSelecionar,
}: {
  id: string;
  animal: Animal;
  avaliacoes: Avaliacao[];
  medidas: Medida[];
  ficha: Avaliacao;
  pop: Populacao;
  onSelecionar: (id: string) => void;
}) {
  const [codigo, setCodigo] = useState('A_CER');
  // Uma ficha ja basta: o ponto aparece contra a faixa do esperado para a idade.
  const disponiveis = CONTINUAS.filter((t) => medidas.some((m) => m.trait_code === t.code && m.value_num != null));
  const trait = traitPorCodigo.get(codigo);

  const pontos = avaliacoes.flatMap((a) => {
    const m = medidas.find((x) => x.assessment_id === a.id && x.trait_code === codigo);
    if (m?.value_num == null) return [];
    const meses = idadeEmMeses(animal.birth_date, a.visit_date);
    return [
      {
        id: a.id,
        x: meses,
        y: m.value_num,
        rotuloX: formatarIdade(meses),
        rotuloY: `${formatarValor(m.value_num, trait)}${unidadeCurta(trait)}`,
      },
    ];
  });

  const idades = pontos.map((p) => p.x);
  const referencia = idades.length ? serieDeReferencia(pop, animal, codigo, Math.min(...idades) - 3, Math.max(...idades) + 3) : [];
  const posicao = posicaoNaIdade(pop, animal, ficha, codigo);

  return (
    <section id={id} aria-label="Evolução" className="scroll-mt-28">
      <Painel
        titulo={`Evolução${trait && disponiveis.length ? ` — ${trait.label}` : ''}`}
        acoes={
          disponiveis.length > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-apagado">Medida</span>
              <select
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                className="rounded-(--radius-controle) border border-borda-forte bg-superficie px-2 text-sm"
              >
                {disponiveis.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          )
        }
      >
        {disponiveis.length === 0 || pontos.length === 0 ? (
          <p className="text-sm text-apagado">A evolução aparece quando o animal tiver uma medida contínua registrada.</p>
        ) : (
          <>
            <GraficoEvolucao
              pontos={pontos}
              selecionado={ficha.id}
              onSelecionar={onSelecionar}
              titulo={trait?.label ?? codigo}
              referencia={referencia}
            />
            {posicao && <p className="mt-3 text-sm">{frasePosicao(posicao, codigo)}</p>}
            <p className="mt-2 text-xs text-apagado">
              {referencia.length > 0 && 'Faixa sombreada: o esperado para a idade, pela média dos animais medidos. '}
              {pontos.length > 1 && 'Clique num ponto para abrir aquela ficha.'}
            </p>
          </>
        )}
      </Painel>
    </section>
  );
}

function Midias({ id, animalId, fichaId, equipe }: { id: string; animalId: string; fichaId: string; equipe: boolean }) {
  const consulta = useConsulta(
    async (f) => {
      const midias = await f.midias(fichaId);
      const analisadas = new Set<string>();
      for (const m of midias) if ((await f.analises(m.id)).some((a) => !a.deleted_at)) analisadas.add(m.id);
      return { midias, analisadas };
    },
    [fichaId],
  );

  return (
    <section id={id} aria-label="Fotos e vídeos" className="scroll-mt-28">
      <Painel
        titulo="Fotos e vídeos"
        acoes={equipe ? <AnexarMidia avaliacaoId={fichaId} /> : <span className="text-xs text-apagado">Clique para abrir e analisar</span>}
      >
        <Aguardar consulta={consulta} carregando={<div className="esqueleto h-40" />}>
          {({ midias, analisadas }) => {
            if (midias.length === 0) return <p className="text-sm text-apagado">Nenhuma mídia gravada nesta avaliação.</p>;
            const fotos = midias.filter((m) => m.kind === 'foto');
            const videos = midias.filter((m) => m.kind === 'video');
            return (
              <div className="space-y-5">
                {fotos.length > 0 && <GradeMidias titulo="Fotos" midias={fotos} animalId={animalId} analisadas={analisadas} />}
                {videos.length > 0 && <GradeMidias titulo="Vídeos" midias={videos} animalId={animalId} analisadas={analisadas} />}
              </div>
            );
          }}
        </Aguardar>
      </Painel>
    </section>
  );
}

function GradeMidias({
  titulo,
  midias,
  animalId,
  analisadas,
}: {
  titulo: string;
  midias: Midia[];
  animalId: string;
  analisadas: Set<string>;
}) {
  return (
    <div className="space-y-2">
      <h3 className="rotulo-secao">{titulo}</h3>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {midias.map((m) => (
          <li key={m.id}>
            <Link
              to={`/animais/${animalId}/midia/${m.id}`}
              className="group block overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie transition-colors hover:border-destaque"
            >
              <div className="relative flex aspect-video items-center justify-center bg-abismo text-nevoa">
                {m.kind === 'video' ? (
                  <IconeVideo width={28} height={28} />
                ) : m.storage_path || m.pendente ? (
                  <FotoDaMidia midia={m} className="absolute inset-0 size-full" />
                ) : (
                  <IconeCamera width={28} height={28} />
                )}
                {analisadas.has(m.id) && (
                  <span className="absolute top-2 left-2 bg-gelo px-1.5 py-0.5 text-xs font-semibold tracking-wide text-abismo uppercase">
                    Analisada
                  </span>
                )}
              </div>
              <div className="px-3 py-2">
                <p className="text-sm font-medium group-hover:underline">
                  {m.kind === 'foto' ? (m.view ? ROTULO_VISTA[m.view] : 'Foto') : m.view ? ROTULO_VISTA[m.view] : 'Vídeo'}
                </p>
                <p className="text-xs text-apagado">
                  {m.gait ? ROTULO_ANDAMENTO[m.gait] : 'Estação 2'} ·{' '}
                  {m.storage_path ? 'arquivo enviado' : m.pendente ? 'neste aparelho, aguardando envio' : 'arquivo não enviado'}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Prole({ filhos, todos, pop }: { filhos: Animal[]; todos: Animal[]; pop: Populacao }) {
  const hoje = hojeIso();
  const porMedida = MEDIDAS_DA_PROLE.map((c) => proleNaMedida(pop, filhos, c));
  const altura = porMedida[0]!;
  return (
    <Painel titulo={`Prole cadastrada (${filhos.length})`} corpo="p-0">
      {filhos.length === 0 ? (
        <p className="px-4 py-4 text-sm text-apagado">Nenhum filho cadastrado.</p>
      ) : (
        <Tabela rotulo="Prole">
          <thead>
            <tr>
              <th scope="col" className={classeTh}>
                Animal
              </th>
              <th scope="col" className={classeTh}>
                Classe
              </th>
              <th scope="col" className={classeTh}>
                Sexo
              </th>
              <th scope="col" className={`${classeTh} text-right`}>
                Idade
              </th>
              <th scope="col" className={`${classeTh} hidden text-right sm:table-cell`}>
                Altura vs. esperado
              </th>
            </tr>
          </thead>
          <tbody>
            {filhos.map((f) => (
              <tr key={f.id}>
                <td className={classeTd}>
                  <Link to={`/animais/${f.id}`} className="font-serif font-semibold text-destaque-texto hover:underline">
                    {f.name}
                  </Link>
                </td>
                <td className={classeTd}>
                  <Etiqueta>{ROTULO_CLASSE[classeNaData(f, todos, hoje)]}</Etiqueta>
                </td>
                <td className={`${classeTd} text-apagado`}>{ROTULO_SEXO[f.sex]}</td>
                <td className={`${classeTd} numero text-right`}>{formatarIdade(idadeEmMeses(f.birth_date, hoje))}</td>
                <td className={`${classeTd} numero hidden text-right sm:table-cell`}>
                  <DiferencaDoEsperado desvio={altura.porFilho.get(f.id)} codigo="A_CER" />
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
      {filhos.length > 0 && (
        <div className="space-y-2 border-t border-borda bg-superficie-2 px-4 py-3 text-sm">
          <p className="font-semibold">Média da prole</p>
          <ul className="space-y-1 text-apagado">
            {porMedida.map((p) => (
              <li key={p.codigo}>{fraseProle(p)}</li>
            ))}
          </ul>
          <p className="text-xs text-apagado">
            Cada filho é comparado com o esperado para a idade dele, pela última ficha. É uma média simples: a avaliação
            genética (Fase 3) desconta também o outro genitor e o ambiente.
          </p>
        </div>
      )}
    </Painel>
  );
}
